import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Trophy, Flame, Calendar, IdCard, MessageSquare, ArrowUpRight, Activity } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
}
function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

export default function Dashboard() {
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    api.get("/tournaments", { params: { status: "upcoming" } }).then((r) => setTournaments(r.data.slice(0, 3)));
    api.get("/leaderboard", { params: { limit: 5 } }).then((r) => setLeaderboard(r.data));
  }, []);

  return (
    <div className="space-y-10 fade-in" data-testid="dashboard-page">
      {/* Greeting */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary">Dashboard</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Hallo, {user?.name?.split(" ")[0] || "Athlet"}.
          </h1>
          <p className="text-white/60 mt-2 text-sm">
            Hier ist deine aktuelle Übersicht. Viel Glück in der laufenden Season.
          </p>
        </div>
        <Link to="/athlete-id" className="btn-primary" data-testid="dashboard-view-id-btn">
          <IdCard className="w-4 h-4" /> Sportlerausweis
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5">
        {[
          { label: "Member ID", value: user?.member_id || "—", icon: IdCard },
          { label: "Game", value: user?.favorite_game || "EA FC 26", icon: Trophy },
          { label: "Role", value: user?.role?.toUpperCase() || "MEMBER", icon: Activity },
          { label: "Valid until", value: formatDate(user?.valid_until), icon: Calendar },
        ].map((s) => (
          <div key={s.label} className="bg-bg p-5">
            <div className="flex items-center gap-2 text-white/40">
              <s.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">{s.label}</span>
            </div>
            <div className="font-mono tabular-nums text-xl mt-3 truncate" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Upcoming Tournaments */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Upcoming</div>
            <h2 className="font-display text-2xl tracking-tight mt-1">Nächste Wettkämpfe</h2>
          </div>
          <Link to="/tournaments" className="text-white/60 link-underline text-sm" data-testid="dashboard-all-tournaments">
            Alle →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tournaments.length === 0 && (
            <div className="col-span-3 text-white/40 text-sm font-mono p-6 border border-dashed border-white/10">
              Keine anstehenden Turniere.
            </div>
          )}
          {tournaments.map((t) => (
            <Link
              key={t.id}
              to={`/tournaments/${t.id}`}
              data-testid={`dashboard-tournament-card-${t.id}`}
              className="card-solid p-5 hover:border-primary/50 transition-colors group"
            >
              <div className="flex items-center gap-2 text-primary">
                <Flame className="w-3.5 h-3.5" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em]">{t.game}</span>
              </div>
              <div className="font-display text-xl mt-3 tracking-tight">{t.name}</div>
              <div className="mt-3 text-white/60 text-xs font-mono">
                {formatDate(t.start_time)} · {formatTime(t.start_time)}
              </div>
              <div className="flex items-center justify-between mt-5">
                <span className="chip text-white/60">{t.participants?.length || 0}/{t.max_players}</span>
                <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-primary" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Mini Leaderboard */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Top 5</div>
            <h2 className="font-display text-2xl tracking-tight mt-1">Rangliste</h2>
          </div>
          <Link to="/leaderboard" className="text-white/60 link-underline text-sm" data-testid="dashboard-full-leaderboard">
            Komplette Liste →
          </Link>
        </div>
        <div className="card-solid overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/40">
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.2em]">#</th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.2em]">Spieler</th>
                <th className="text-right p-3 font-mono text-[10px] uppercase tracking-[0.2em]">Punkte</th>
                <th className="text-right p-3 font-mono text-[10px] uppercase tracking-[0.2em] hidden sm:table-cell">Siege</th>
                <th className="text-right p-3 font-mono text-[10px] uppercase tracking-[0.2em] hidden sm:table-cell">Win%</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-white/40 text-sm font-mono">Keine Einträge vorhanden.</td></tr>
              )}
              {leaderboard.map((e) => (
                <tr key={e.user_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`dashboard-lb-row-${e.user_id}`}>
                  <td className="p-3 font-mono tabular-nums">
                    <span className={e.rank === 1 ? "text-primary" : e.rank === 2 ? "text-magenta" : e.rank === 3 ? "text-accent" : "text-white/50"}>
                      {String(e.rank).padStart(2, "0")}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {e.picture ? (
                        <img src={e.picture} alt="" className="w-6 h-6 object-cover rounded-sm border border-white/10" />
                      ) : (
                        <div className="w-6 h-6 bg-primary/10 border border-primary/30 text-primary flex items-center justify-center font-mono text-[10px] rounded-sm">
                          {e.name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <span className="truncate">{e.name}</span>
                    </div>
                  </td>
                  <td className="p-3 text-right font-mono tabular-nums">{e.points}</td>
                  <td className="p-3 text-right font-mono tabular-nums hidden sm:table-cell">{e.wins}</td>
                  <td className="p-3 text-right font-mono tabular-nums hidden sm:table-cell">{e.win_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Chat teaser */}
      <section>
        <Link to="/chat" className="card p-6 flex items-center justify-between group hover:border-primary/40 transition-colors" data-testid="dashboard-chat-link">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center border border-primary/30 bg-primary/10 text-primary rounded-sm">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display text-lg tracking-tight">Vereins-Chat</div>
              <div className="text-xs text-white/50 font-mono uppercase tracking-[0.2em]">#general · #ea-fc-26 · #turniere</div>
            </div>
          </div>
          <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-primary" />
        </Link>
      </section>
    </div>
  );
}
